import Dexie from 'dexie'
import type { MinimindDB } from '../../types/database'

const DB_NAME = 'minimind'
const MAX_OPEN_RETRIES = 3
const RETRY_DELAY_MS = 300

export const db = new Dexie(DB_NAME) as MinimindDB

db.version(7).stores({
  notes: '++id, contextSlot, deletedAt, syncedAt, [contextSlot+deletedAt]',
  lists: '++id, contextSlot, deletedAt, syncedAt, [contextSlot+deletedAt]',
  contexts: '++id, slot, deletedAt, syncedAt',
  labels: '++id, contextSlot, deletedAt, syncedAt, [contextSlot+deletedAt]',
  tasks: '++id, contextSlot, deletedAt, syncedAt, [contextSlot+deletedAt]',
  references: '++id, name, deletedAt, syncedAt',
  preferences: '++key',
  changes: '++id, [table+itemId], timestamp'
})

db.version(8)
  .stores({
    notes:
      '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
    lists:
      '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
    contexts: '++id, slot, deletedAt, syncedAt',
    labels:
      '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
    tasks:
      '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
    references: '++id, name, deletedAt, syncedAt',
    preferences: '++key',
    changes: '++id, [table+itemId], timestamp'
  })
  .upgrade(async (tx) => {
    // Build a map of slot → context id from existing contexts.
    // For each content item, set contextId from the matching context by slot.
    // Items whose slot has no matching context get contextId = '' (not silently lost).
    const contexts = await tx.table('contexts').toArray()
    const slotToId = new Map<number, string>(
      contexts.map((c: { slot: number; id: string }) => [c.slot, c.id])
    )

    for (const table of ['notes', 'tasks', 'lists', 'labels']) {
      const items = await tx.table(table).toArray()
      for (const item of items) {
        const contextId = slotToId.get(item.contextSlot) ?? null
        await tx.table(table).update(item.id, { contextId })
      }
    }
  })

db.version(9).upgrade(async (tx) => {
  // Delete orphaned labels (no valid contextId) — labels not yet in production
  const labels = await tx.table('labels').toArray()
  for (const label of labels) {
    if (!label.contextId) {
      await tx.table('labels').delete(label.id)
    }
  }
})

db.version(10).stores({
  notes:
    '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  lists:
    '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  contexts: '++id, slot, deletedAt, syncedAt',
  labels:
    '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  tasks:
    '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  entries:
    '++id, contextSlot, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  references: '++id, name, deletedAt, syncedAt',
  preferences: '++key',
  changes: '++id, [table+itemId], timestamp'
})

db.version(11).stores({
  notes: '++id, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  lists: '++id, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  contexts: '++id, deletedAt, syncedAt',
  labels: '++id, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  tasks: '++id, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  entries: '++id, contextId, deletedAt, syncedAt, [contextId+deletedAt]',
  references: '++id, name, deletedAt, syncedAt',
  preferences: '++key',
  changes: '++id, [table+itemId], timestamp'
})

/**
 * Opens the database with retry logic to handle Chrome's transient
 * IndexedDB `UnknownError`. When the error occurs Dexie closes the DB
 * internally, so each retry calls `db.open()` to get a fresh connection.
 */
async function openWithRetry(): Promise<void> {
  for (let attempt = 1; attempt <= MAX_OPEN_RETRIES; attempt++) {
    try {
      if (!db.isOpen()) {
        await db.open()
      }
      await Promise.all([
        db.notes.count(),
        db.lists.count(),
        db.contexts.count(),
        db.labels.count(),
        db.tasks.count(),
        db.entries.count(),
        db.changes.count(),
        db.preferences.count()
      ])
      return
    } catch (error) {
      console.warn(
        `[minimind] DB open attempt ${attempt}/${MAX_OPEN_RETRIES} failed:`,
        error
      )
      if (attempt < MAX_OPEN_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY_MS * attempt)
        )
      } else {
        throw error
      }
    }
  }
}

await openWithRetry()
